import ShopifyObject from './ShopifyObject';
import Address from './Address';

export default class Customer extends ShopifyObject {
  /**
   * @param {ShopifyCustomer} customer
   */
  constructor(customer) {
    super(customer.id);

    this._email = customer.email || null;
    this._acceptsMarketing = customer.accepts_marketing || false;
    this._createdAt = customer.created_at || new Date(0);
    this._updatedAt = customer.updated_at || new Date(0);
    this._firstName = customer.first_name || '';
    this._lastName = customer.last_name || '';
    this._ordersCount = customer.orders_count || 0;
    this._state = customer.state || 'disabled';
    this._totalSpent = customer.total_spent || 0;
    this._lastOrderId = customer.last_order_id || null;
    this._note = customer.note || '';
    this._verifiedEmail = customer.verified_email || true;
    this._multipassIdentifier = customer.multipass_identifier || null;
    this._taxExempt = customer.tax_exempt || false;
    this._phone = customer.phone || null;
    this._tags = customer.tags || '';
    this._lastOrderName = customer.last_order_name || null;
    this._currency = customer.currency || '';
    this._addresses =
      customer.addresses.map(address => new Address(address)) || [];
    this._acceptsMarketingUpdatedAt =
      customer.accepts_marketing_updated_at || new Date(0);
    this._marketingOptInLevel = customer.marketing_opt_in_level || null;
    this._defaultAddress = new Address(customer.default_address) || [];
  }

  /**
   * @returns {string}
   */
  get email() {
    return this._email;
  }

  /**
   * @param {string}
   */
  set email(email) {
    this._email = email;
  }

  /**
   * @returns {boolean}
   */
  get acceptsMarketing() {
    return this._acceptsMarketing;
  }

  /**
   * @param {boolean}
   */
  set acceptsMarketing(acceptsMarketing) {
    this._acceptsMarketing = acceptsMarketing;
  }

  /**
   * @returns {Date}
   */
  get createdAt() {
    return this._createdAt;
  }

  /**
   * @returns {Date}
   */
  get updatedAt() {
    return this._updatedAt;
  }

  /**
   * @returns {string}
   */
  get firstName() {
    return this._firstName;
  }

  /**
   * @param {string}
   */
  set firstName(firstName) {
    this._firstName = firstName;
  }

  /**
   * @returns {string}
   */
  get lastName() {
    return this._lastName;
  }

  /**
   * @param {string}
   */
  set lastName(lastName) {
    this._lastName = lastName;
  }

  /**
   * @returns {number}
   */
  get ordersCount() {
    return this._ordersCount;
  }

  /**
   * @returns {string}
   */
  get state() {
    return this._state;
  }

  /**
   * @param {string}
   */
  set state(state) {
    this._state = state;
  }

  /**
   * @returns {number}
   */
  get totalSpent() {
    return this._totalSpent;
  }

  /**
   * @returns {number}
   */
  get lastOrderId() {
    return this._lastOrderId;
  }

  /**
   * @returns {string}
   */
  get note() {
    return this._note;
  }

  /**
   * @param {string}
   */
  set note(note) {
    this._note = note;
  }

  /**
   * @returns {string}
   */
  get verifiedEmail() {
    return this._verifiedEmail;
  }

  /**
   * @returns {string}?
   */
  get multipassIdentifier() {
    return this._multipassIdentifier;
  }

  /**
   * @returns {boolean}
   */
  get taxExempt() {
    return this._taxExempt;
  }

  /**
   * @param {boolean}
   */
  set taxExempt(taxExempt) {
    this._taxExempt = taxExempt;
  }

  /**
   * @returns {string}
   */
  get phone() {
    return this._phone;
  }

  /**
   * @param {string}
   */
  set phone(phone) {
    this._phone = phone;
  }

  /**
   * @returns {string}
   */
  get tags() {
    return this._tags;
  }

  /**
   * @param {string}
   */
  set tags(tags) {
    this._tags = tags;
  }

  /**
   * @returns {string}
   */
  get lastOrderName() {
    return this._lastOrderName;
  }

  /**
   * @returns {string}
   */
  get currency() {
    return this._currency;
  }

  /**
   * @returns {Address[]}
   */
  get addresses() {
    return this._addresses;
  }

  /**
   * @param {Address[]}
   */
  set addresses(addresses) {
    this._addresses = addresses;
  }

  /**
   * @returns {boolean}
   */
  get acceptsMarketingUpdatedAt() {
    return this._acceptsMarketingUpdatedAt;
  }

  /**
   * @param {boolean}
   */
  set acceptsMarketingUpdatedAt(acceptsMarketingUpdatedAt) {
    this._acceptsMarketingUpdatedAt = acceptsMarketingUpdatedAt;
  }

  /**
   * @returns {string}
   */
  get marketingOptInLevel() {
    return this._marketingOptInLevel;
  }

  /**
   * @returns {Address[]}
   */
  get defaultAddress() {
    return this._defaultAddress;
  }

  /**
   * @param {Address[]}
   */
  set defaultAddress(defaultAddress) {
    this._defaultAddress = defaultAddress;
  }

  /**
   *
   * @return {Object} - Shopify Customer Object.
   */
  JSON() {
    return {
      accepts_marketing: this.acceptsMarketing,
      accepts_marketing_updated_at: this.acceptsMarketingUpdatedAt,
      addresses: this.addresses.map(address => address.JSON()),
      created_at: this.createdAt,
      currency: this.currency,
      default_address: this.defaultAddress.JSON(),
      email: this.email,
      first_name: this.firstName,
      id: super.id,
      last_name: this.lastName,
      last_order_id: this.lastOrderId,
      last_order_name: this.lastOrderName,
      marketing_opt_in_level: this.marketingOptInLevel,
      multipass_identifier: this.multipassIdentifier,
      note: this.note,
      orders_count: this.ordersCount,
      phone: this.phone,
      state: this.state,
      tags: this.tags,
      tax_exempt: this.taxExempt,
      total_spent: this.totalSpent,
      updated_at: this.updatedAt,
      verified_email: this.verifiedEmail,
    };
  }
}
